import { HttpClient } from "common/services/http-client.service";
import { withApiResponse } from "common/utils/rtk-query.util";
import { documentsConfig } from "../config/document.config";
import { ApiResult } from "common/models";
import {
  DocumentDetail,
  DocumentList,
  DocumentRequirements,
  ListParam,
  NonExistingDocument,
  UpdateDocumentResult
} from "../models/document.model";
import { List } from "common/models/list.model";
import {
  AddTagRequest,
  DeleteMultipleTagRequest,
  DeleteTagRequest,
  EditRewrittenRequirementRequest,
  RecommendedTagsResponse,
  RedoTaggingRequest,
  RequirementRecommendedTagsResponse,
  Tag,
  TagListRequest,
  TagListResponse,
  TagsBySearchTermRequest,
  TagsBySearchTermResponse,
  TagsResponse
} from "../models/tags.model";
import { ClassListResponse } from "../models/class.model";
import { AddRuleRequest, FailedRulesRequest } from "../models/rules.model";
import {
  DeleteFailedRuleRequest,
  FailedRulesResponse
} from "../models/failed-rules.model";
import {
  AddRewrittenRequirementRequest,
  DeleteRewrittenRequirementRequest,
  TriggerFinalizeDocumentRequest,
  TriggerRewriteRequest,
  TriggerTaggingRequest,
  TriggerTaggingWithoutRewriteRequest
} from "../models/rewritten-requirement.model";
import {
  AddRequirementRequest,
  DeleteMultipleRequirementsRequest,
  DeleteMultipleRequirementsResponse,
  ReEvaluateReferencesRequest,
  ReEvaluateReferencesResponse,
  ReEvaluateSimilarRequirementsRequest,
  ReEvaluateSimilarRequirementsResponse,
  ReferenceDataRequest,
  ReferenceDataResponse,
  RevertRequirementRequest,
  RevertRequirementResponse,
  RewriteRequirementRequest,
  SimilarRequirementsListRequest,
  SimilarRequirementsListResponse,
  UpdateRequirementNotesRequest,
  UpdateRequirementRequest,
  UpdateSimilarRequirementsFeedbackRequest
} from "../models/requirements.model";
import {
  AddRequirementToHierarchyRequest,
  AddRequirementToHierarchyResponse
} from "../models/add-requirement-to-hierarchy.model";
import {
  RemoveRequirementFromHierarchyRequest,
  RemoveRequirementFromHierarchyResponse
} from "../models/remove-requirement-from-hierarchy.model";
import {
  ReorderRewrittenRequirementRequest,
  ReorderRewrittenRequirementResponse
} from "../models/reorder-rewritten-requirement.model";
import {
  DocumentUpdateRequest,
  DocumentUploadRequest
} from "common/models/document.model";

export class DocumentService extends HttpClient {
  public static getAllDocuments(params: ListParam) {
    const config = documentsConfig.getAllDocuments(params);
    return withApiResponse<List<DocumentList>>(async () => {
      const response = await this.invokeApi<List<DocumentList>>(config);
      return new ApiResult<List<DocumentList>>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getAdminDocuments(params: any) {
    const config = documentsConfig.getAdminDocuments(params);
    return withApiResponse<List<DocumentList>>(async () => {
      const response = await this.invokeApi<List<DocumentList>>(config);
      return new ApiResult<List<DocumentList>>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateDocument(
    documentId: string,
    documentUpdateRequest: DocumentUpdateRequest
  ) {
    const config = documentsConfig.updateDocument(
      documentId,
      documentUpdateRequest
    );
    return withApiResponse<UpdateDocumentResult>(async () => {
      const response = await this.invokeApi<UpdateDocumentResult>(config);
      return new ApiResult<UpdateDocumentResult>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getDocumentDetails(documentId: string) {
    const config = documentsConfig.getDoumentDetail(documentId);
    return withApiResponse<DocumentDetail>(async () => {
      const response = await this.invokeApi<DocumentDetail>(config);
      return new ApiResult<DocumentDetail>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getNonExistingDocuments(documentId: string) {
    const config = documentsConfig.getNonExistingDocuments(documentId);
    return withApiResponse<NonExistingDocument>(async () => {
      const response = await this.invokeApi<NonExistingDocument>(config);
      return new ApiResult<NonExistingDocument>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getDocumentRequirements(documentId: string, params: ListParam) {
    const config = documentsConfig.getDocumentRequirements(documentId, params);
    return withApiResponse<DocumentRequirements>(async () => {
      const response = await this.invokeApi<DocumentRequirements>(config);
      return new ApiResult<DocumentRequirements>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static uploadDocument(documentUploadRequest: DocumentUploadRequest) {
    const config = documentsConfig.uploadDocument(documentUploadRequest);
    return withApiResponse<List<DocumentList>>(async () => {
      const response = await this.invokeApi<List<DocumentList>>(config, {
        "Content-Type": "multipart/form-data"
      });
      return new ApiResult<List<DocumentList>>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static deleteDocument(documentId: string) {
    const config = documentsConfig.deleteDocument(documentId);
    return withApiResponse<void>(async () => {
      const response = await this.invokeApi<void>(config);
      return new ApiResult<void>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  // public static getDocumentTags(documentId: string) {
  //   const config = documentsConfig.getDocumentTags(documentId);
  //   return withApiResponse<List<TagsResponse>>(async () => {
  //     const response = await this.invokeApi<List<TagsResponse>>(config);
  //     return new ApiResult<List<TagsResponse>>(
  //       response.value,
  //       response.statusCode,
  //       ...response.errors
  //     );
  //   });
  // }
  public static async getDocumentTags(documentId: string, reqIds: string[]) {
    const config = documentsConfig.getDocumentTags(documentId, reqIds);
    const response = await this.invokeApi<TagsResponse>(config);
    return response;
  }

  public static async deleteRequirementTag(deleteTagRequest: DeleteTagRequest) {
    const config = documentsConfig.deleteDocumentTag(deleteTagRequest);
    const response = await this.invokeApi<any>(config);
    return response;
  }

  public static async deleteMultipleRequirementTag(
    deleteMultipleTagRequest: DeleteMultipleTagRequest
  ) {
    const config = documentsConfig.deleteMultipleDocumentTag(
      deleteMultipleTagRequest
    );
    const response = await this.invokeApi<any>(config);
    return response;
  }

  public static async downloadDocuments(
    documentId: string,
    documentType: string
  ) {
    const config = documentsConfig.downloadDocuments(documentId, documentType);
    const response = await this.invokeApi<Blob>(config, {
      responseType: "blob",
      "Content-Type": "application/octet-stream"
    });
    return response;
  }

  public static getTagsList(tagListRequestObj: TagListRequest) {
    const config = documentsConfig.getTagsList(tagListRequestObj);
    return withApiResponse<TagListResponse>(async () => {
      const response = await this.invokeApi<TagListResponse>(config);
      return new ApiResult<TagListResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getTagsBySearchTerm(request: TagsBySearchTermRequest) {
    const config = documentsConfig.getTagsBySearchTerm(request);
    return withApiResponse<TagsBySearchTermResponse>(async () => {
      const response = await this.invokeApi<TagsBySearchTermResponse>(config);
      return new ApiResult<TagsBySearchTermResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static addTags(addTagRequestObj: AddTagRequest) {
    const config = documentsConfig.addTags(addTagRequestObj);
    return withApiResponse<void>(async () => {
      const response = await this.invokeApi<void>(config);
      return new ApiResult<void>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static editRewrittenRequirement(
    editRewrittenRequirementRequestObj: EditRewrittenRequirementRequest
  ) {
    const config = documentsConfig.editRewrittenRequirement(
      editRewrittenRequirementRequestObj
    );
    return withApiResponse<EditRewrittenRequirementRequest>(async () => {
      const response = await this.invokeApi<EditRewrittenRequirementRequest>(
        config
      );
      return new ApiResult<EditRewrittenRequirementRequest>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getClassList() {
    const config = documentsConfig.getClassList();
    return withApiResponse<ClassListResponse>(async () => {
      const response = await this.invokeApi<ClassListResponse>(config);
      return new ApiResult<ClassListResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getFailedRules(failedRulesRequest: FailedRulesRequest) {
    const config = documentsConfig.getFailedRules(failedRulesRequest);
    return withApiResponse<FailedRulesResponse>(async () => {
      const response = await this.invokeApi<FailedRulesResponse>(config);
      return new ApiResult<FailedRulesResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static addRewrittenRequirement(
    addRewrittenRequirement: AddRewrittenRequirementRequest
  ) {
    const config = documentsConfig.addRewrittenRequirement(
      addRewrittenRequirement
    );
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static async deleteRewrittenRequirement(
    deleteRewrittenRequirement: DeleteRewrittenRequirementRequest
  ) {
    const config = documentsConfig.deleteRewrittenRequirement(
      deleteRewrittenRequirement
    );
    const response = await this.invokeApi<any>(config);
    return response;
  }

  public static async deleteOriginalRequirement(
    deleteRewrittenRequirement: DeleteRewrittenRequirementRequest
  ) {
    const config = documentsConfig.deleteOriginalRequirement(
      deleteRewrittenRequirement
    );
    const response = await this.invokeApi<any>(config);
    return response;
  }

  public static triggerReWrite(triggerRewriteRequest: TriggerRewriteRequest) {
    const config = documentsConfig.triggerReWrite(triggerRewriteRequest);
    return withApiResponse<void>(async () => {
      const response = await this.invokeApi<void>(config);
      return new ApiResult<void>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static triggerTagging(triggerTaggingRequest: TriggerTaggingRequest) {
    const config = documentsConfig.triggerTagging(triggerTaggingRequest);
    return withApiResponse<void>(async () => {
      const response = await this.invokeApi<void>(config);
      return new ApiResult<void>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static triggerTaggingWithoutRewrite(
    triggerTaggingWithoutRewriteRequest: TriggerTaggingWithoutRewriteRequest
  ) {
    const config = documentsConfig.triggerTaggingWithoutRewrite(
      triggerTaggingWithoutRewriteRequest
    );
    return withApiResponse<void>(async () => {
      const response = await this.invokeApi<void>(config);
      return new ApiResult<void>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static async deleteFailedRule(
    deleteFailedRule: DeleteFailedRuleRequest
  ) {
    const config = documentsConfig.deleteFailedRule(deleteFailedRule);
    const response = await this.invokeApi<any>(config);
    return response;
  }

  public static addRule(addRuleRequest: AddRuleRequest) {
    const config = documentsConfig.addRule(addRuleRequest);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static addRequirement(addRequirement: AddRequirementRequest) {
    const config = documentsConfig.addRequirement(addRequirement);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateRequirement(updateRequirement: UpdateRequirementRequest) {
    const config = documentsConfig.updateRequirement(updateRequirement);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static rewriteRequirement(
    rewriteRequirement: RewriteRequirementRequest
  ) {
    const config = documentsConfig.rewriteRequirement(rewriteRequirement);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static redoTagging(redoTagging: RedoTaggingRequest) {
    const config = documentsConfig.redoTagging(redoTagging);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static triggerFinalizeDocument(
    triggerFinalizeDocumentRequest: TriggerFinalizeDocumentRequest
  ) {
    const config = documentsConfig.triggerFinalizeDocument(
      triggerFinalizeDocumentRequest
    );
    return withApiResponse<void>(async () => {
      const response = await this.invokeApi<void>(config);
      return new ApiResult<void>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getRewrittenRequirementRecommendedTags(
    documentGuid: string,
    rewrittenRequirementGuids: string[]
  ) {
    const config = documentsConfig.getRewrittenRequirementRecommendedTags(
      documentGuid,
      rewrittenRequirementGuids
    );

    return withApiResponse<RecommendedTagsResponse>(async () => {
      const response = await this.invokeApi<RecommendedTagsResponse>(config);
      return new ApiResult<RecommendedTagsResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getReferenceData(request: ReferenceDataRequest) {
    const config = documentsConfig.getReferenceData(request);
    return withApiResponse<ReferenceDataResponse>(async () => {
      const response = await this.invokeApi<ReferenceDataResponse>(config);
      return new ApiResult<ReferenceDataResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getNotes(requirementGuid: string, isRewrite: boolean) {
    const config = documentsConfig.getNotes(requirementGuid, isRewrite);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static deleteNotes(requirementGuid: string, isRewrite: boolean) {
    const config = documentsConfig.deleteNotes(requirementGuid, isRewrite);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateNotes(request: UpdateRequirementNotesRequest) {
    const config = documentsConfig.updateNotes(request);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<UpdateRequirementNotesRequest>(
        config
      );
      return new ApiResult<UpdateRequirementNotesRequest>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static addNotes(request: UpdateRequirementNotesRequest) {
    const config = documentsConfig.addNotes(request);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<UpdateRequirementNotesRequest>(
        config
      );
      return new ApiResult<UpdateRequirementNotesRequest>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static reEvaluateReferences(request: ReEvaluateReferencesRequest) {
    const config = documentsConfig.reEvaluateReferences(request);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<ReEvaluateReferencesResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static revertRequirement(request: RevertRequirementRequest) {
    const config = documentsConfig.revertRequirement(request);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<RevertRequirementResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getSimilarRequirementsList(
    request: SimilarRequirementsListRequest
  ) {
    const config = documentsConfig.getSimilarRequirementsList(request);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<SimilarRequirementsListResponse>(
        config
      );
      return new ApiResult<SimilarRequirementsListResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateSimilarRequirementsFeedback(
    request: UpdateSimilarRequirementsFeedbackRequest
  ) {
    const config = documentsConfig.updateSimilarRequirementsFeedback(request);
    return withApiResponse<any>(async () => {
      const response =
        await this.invokeApi<UpdateSimilarRequirementsFeedbackRequest>(config);
      return new ApiResult<UpdateSimilarRequirementsFeedbackRequest>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static deleteSimilarRequirement(request: any) {
    const config = documentsConfig.deleteSimilarRequirement(request);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static exportDocument(exportDocument: string) {
    const config = documentsConfig.exportDocument(exportDocument);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static similaritySearch(documentGuid: string) {
    const config = documentsConfig.similaritySearch(documentGuid);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static async deleteMultipleOriginalAndRewrittenReq(
    request: DeleteMultipleRequirementsRequest
  ) {
    const config =
      documentsConfig.deleteMultipleOriginalAndRewrittenReq(request);
    const response = await this.invokeApi<DeleteMultipleRequirementsResponse>(
      config
    );
    return new ApiResult<DeleteMultipleRequirementsResponse>(
      response.value,
      response.statusCode,
      ...response.errors
    );
  }

  public static getRecommendedTagsForRewrittenRequirements(
    documentGuid: string,
    rewrittenRequirementGuids: string[]
  ) {
    const config = documentsConfig.getRecommendedTagsForRewrittenRequirements(
      documentGuid,
      rewrittenRequirementGuids
    );

    return withApiResponse<RequirementRecommendedTagsResponse>(async () => {
      const response = await this.invokeApi<RequirementRecommendedTagsResponse>(
        config
      );
      return new ApiResult<RequirementRecommendedTagsResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static addRequirementToHierarchy(
    request: AddRequirementToHierarchyRequest
  ) {
    const config = documentsConfig.addRequirementToHierarchy(request);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<AddRequirementToHierarchyResponse>(
        config
      );
      return new ApiResult<AddRequirementToHierarchyResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static removeRequirementFromHierarchy(
    request: RemoveRequirementFromHierarchyRequest
  ) {
    const config = documentsConfig.removeRequirementFromHierarchy(request);
    return withApiResponse<any>(async () => {
      const response =
        await this.invokeApi<RemoveRequirementFromHierarchyResponse>(config);
      return new ApiResult<RemoveRequirementFromHierarchyResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static reorderRewrittenRequirement(
    request: ReorderRewrittenRequirementRequest
  ) {
    const config = documentsConfig.reorderRewrittenRequirement(request);
    return withApiResponse<any>(async () => {
      const response =
        await this.invokeApi<ReorderRewrittenRequirementResponse>(config);
      return new ApiResult<ReorderRewrittenRequirementResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static reEvaluateSimilarRequirements(
    request: ReEvaluateSimilarRequirementsRequest
  ) {
    const config = documentsConfig.reEvaluateSimilarRequirements(request);
    return withApiResponse<any>(async () => {
      const response =
        await this.invokeApi<ReEvaluateSimilarRequirementsResponse>(config);
      return new ApiResult<ReEvaluateSimilarRequirementsResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getMappedDocuments(documentGuid: string) {
    const config = documentsConfig.getMappedDocuments(documentGuid);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static deleteMappedDocument(
    documentGuid: string,
    mappedDocumentGuid: string
  ) {
    const config = documentsConfig.deleteMappedDocument(
      documentGuid,
      mappedDocumentGuid
    );
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static addMappedDocument(
    documentGuid: string,
    mappedDocumentGuid: string
  ) {
    const config = documentsConfig.addMappedDocument(
      documentGuid,
      mappedDocumentGuid
    );
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }
}
